import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { closeModal } from '@src/redux/actions/modal';
import { SUBSCRIBE_ERROR_MESSAGES } from '@src/constants';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { getTotalPriceForSlotBasedSubscription } from '@src/util/pricing';
import { useSetTotalSlotsCountMutation, useUpgradeSubscriptionDataQuery } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import { SlotsCounter, ModalContainer, DetailsList, FooterInfoBlock, ModalActions } from '../components';

const _EditPlanModal = ({ closeModal, setSuccessFlashMessage, setErrorFlashMessage }) => {
  const [slotsCounter, setSlotsCount] = useState(undefined);

  const { loading, error, data } = useUpgradeSubscriptionDataQuery({
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (slotsCounter === undefined) {
        setSlotsCount(get(data, 'customer.planInfo.openingSlots') || 0);
      }
    },
    onError: () => setErrorFlashMessage(),
  });

  const [setTotalSlotsCount, { loading: activateLoading }] = useSetTotalSlotsCountMutation({
    onCompleted: () => {
      setSuccessFlashMessage('Total slots limit has been successfully changed!');
    },
    onError: error => {
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach(graphQLError => {
          const code = get(graphQLError, 'extensions.exception.code');
          setErrorFlashMessage(SUBSCRIBE_ERROR_MESSAGES[code]);
        });
      }
    },
  });

  if (loading || slotsCounter === undefined) {
    return <ModalContainer isLoading />;
  }

  if (error) {
    closeModal();

    return null;
  }

  const {
    customer: {
      id: customerId,
      planInfo: {
        currency: { symbol },
        price,
        openingSlots,
        occupiedOpeningSlots,
        slotPrice,
      },
    },
  } = data;

  const details = [
    {
      name: 'Base Fee',
      value: `${symbol}${price}`,
    },
    {
      name: 'One opening slot',
      value: <strong>Included</strong>,
    },
    {
      name: 'Each additional slot',
      value: `${symbol}${slotPrice} pc.`,
    },
    {
      name: 'Total',
      value: `${symbol}${getTotalPriceForSlotBasedSubscription(price, slotPrice, slotsCounter)}`,
    },
  ];

  const actions = [
    {
      isDisabled: activateLoading,
      name: 'Confirm',
      onClick: () => {
        trackEvent({
          context: MetricContext.BILLING,
          interaction: MetricAction.CHANGE_SLOTS_COUNT,
          payload: {
            'Customer': customerId,
            'New Slots count': slotsCounter,
            'Old Slots count': openingSlots,
          },
        });

        setTotalSlotsCount({
          variables: {
            totalSlotsCount: slotsCounter,
          },
        }).finally(() => closeModal());
      },
    },
    {
      buttonAppearance: 'secondary',
      name: 'Cancel',
      onClick: () => {
        closeModal();
      },
    },
  ];

  return (
    <ModalContainer>
      <SlotsCounter counter={slotsCounter} setCounter={setSlotsCount} slotsInUse={occupiedOpeningSlots} />
      <DetailsList details={details || []} />
      <ModalActions actions={actions || []} />
      <FooterInfoBlock text="You will be charged the price difference for the remainder of your current billing period." />
    </ModalContainer>
  );
};

_EditPlanModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  setErrorFlashMessage: PropTypes.func.isRequired,
  setSuccessFlashMessage: PropTypes.func.isRequired,
};

export const EditPlanModal = connect(null, {
  closeModal,
  setErrorFlashMessage,
  setSuccessFlashMessage,
})(_EditPlanModal);
